import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";

import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";

import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#000000",
      dark: "#535457",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#666666",
      dark: "#950014",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#FC4040",
    },
    info: {
      main: "#178BFF",
      light: "#DAECFF",
    },
    text: {
      primary: "#000000",
      secondary: "#403F45",
      hint: "#808890",
    },

    background: "#FFFFFF",
    backgroundHeader: "#FFFFFF",
    border: "#737278",
    hover: "#DAD9DE",
    placeholder: "#000000",
    transparent: "#00000000",
    divider: "#DAD9DE",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const porsche = {
  themeName: "Porsche",
  brand: BrandsEnum.Porsche,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.divider,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("PorscheNext-Regular"),
    color: variables.colors.text.primary,
    h1: {
      fontSize: "2.5rem",
      lineHeight: "3rem",
      color: variables.colors.text.primary,
      fontWeight: 600,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.25rem",
        lineHeight: "2.75rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontSize: "2rem",
      lineHeight: "2.5rem",
      color: variables.colors.text.primary,
      fontWeight: 600,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.75rem",
        lineHeight: "2.25rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontSize: "1.5rem",
      lineHeight: "2rem",
      color: variables.colors.primary.main,
      fontWeight: 600,
      textDecoration: "underline",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
      },
    },
    h4: {
      color: variables.colors.text.primary,
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
      },
    },
    h5: {
      fontSize: "1.25rem",
      color: variables.colors.primary.main,
      fontWeight: 600,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: "#FFFFFF",
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
      borderRadius: variables.common.borderRadius,
    },
    cardSelected: {
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: variables.colors.transparent,
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiPickersDay: {
      daySelected: {
        "&:hover": {
          color: variables.colors.background,
        },
      },
    },

    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 0,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        borderRadius: variables.common.borderRadius,
        backgroundColor: variables.colors.primary.main,
        fontWeight: 400,
        color: variables.colors.background,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.primary.dark,
          color: variables.colors.background,
          backgroundColor: variables.colors.primary.dark,
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.divider,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.divider,
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.background,
        fontWeight: 400,
        textDecoration: "none",
        border: "1px solid " + variables.colors.primary.main,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          "&:hover": {
            border: "1px solid " + variables.colors.secondary.dark,
            color: variables.colors.secondary.contrastText,
            backgroundColor: variables.colors.secondary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "1px solid " + variables.colors.secondary.dark,
            color: variables.colors.secondary.contrastText,
            backgroundColor: variables.colors.secondary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.secondary.main,
          border: "1px solid " + variables.colors.secondary.main,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.divider,
        },
        "&:active": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.divider,
        },
        color: variables.colors.primary.main,
        border: "1px solid " + variables.colors.primary.main,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.text.primary,
        "&:hover": {
          color: variables.colors.primary.main,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: getFontFamily("PorscheNext-Regular"),
        color: variables.colors.text.primary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        fontFamily: getFontFamily("Porsche Next"),
        color: variables.colors.primary.main,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: 1.75, // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        border: "1px solid " + variables.colors.border,
        backgroundColor: variables.colors.transparent,
        "&:hover": {
          backgroundColor: variables.colors.transparent,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.secondary,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      root: {
        border: "1px solid " + variables.colors.transparent,
        borderLeftWidth: 0,
        "&$selected": {
          borderLeftColor: variables.colors.primary.contrastText,
          fontWeight: 600,
        },
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M11.7 14.4L12 14.625L12.3 14.4L19.5 9V9.00975L12 14.6254L4.5 9.00975V9L11.7 14.4Z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.main,
        stroke: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.primary.main,
          textDecoration: "underline",
          "&:hover": {
            color: variables.colors.primary.dark,
          },
          "&:active": {
            color: variables.colors.primary.dark,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.primary.main,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          fontFamily: getFontFamily("Porsche Next"),
          fontWeight: 600,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          borderColor: variables.colors.primary.main,
          borderWidth: 2,
          "&:hover": {
            fontFamily: getFontFamily("Porsche Next"),
            borderColor: variables.colors.primary.main,
            backgroundColor: variables.colors.transparent,
          },
        },
        "&:hover": {
          fontFamily: getFontFamily("Porsche Next"),
          borderColor: variables.colors.primary.main,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: "#1D1F20",
    textColor: variables.colors.secondary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.text.primary,
    fontWeight: 600,
  },
  icons: {
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#000' stroke='#000' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <svg width='37' height='15' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <g id='Check'>
            <path
              id='Vector'
              d='M17.8482 8.85887L18.2125 8.5054L17.8536 8.14645L17.3536 7.64645L17.0057 7.29864L16.6523 7.6407L10.8057 13.2986L8.35355 10.8464L8 10.4929L7.64645 10.8464L7.14645 11.3464L6.79289 11.7L7.14645 12.0536L10.4464 15.3536L10.7947 15.7018L11.1482 15.3589L17.8482 8.85887ZM20.5 11.5C20.5 15.9239 16.9239 19.5 12.5 19.5C8.07614 19.5 4.5 15.9239 4.5 11.5C4.5 7.07614 8.07614 3.5 12.5 3.5C16.9239 3.5 20.5 7.07614 20.5 11.5Z'
              fill='#F5F5F5'
              stroke='#F5F5F5'
            />
          </g>
        </svg>
      </>
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Close' opacity='0.4'>
          <path
            id='Vector'
            d='M5 19H6.5L12.09 12.83L17.68 19H19.18L12.84 12L19.18 5H17.68L12.09 11.17L6.5 5H5L11.34 12L5 19Z'
            fill='black'
          />
        </g>
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    infoIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Tooltip'>
          <path id='Vector' d='M11.5 7.5H12.5V8.5H11.5V7.5ZM11.5 11.5H12.5V16.5H11.5V11.5Z' fill='#1E369B' stroke='#1E369B' />
          <path
            id='Vector_2'
            d='M12 3C10.22 3 8.47991 3.52784 6.99987 4.51677C5.51983 5.50571 4.36628 6.91131 3.68509 8.55585C3.0039 10.2004 2.82567 12.01 3.17294 13.7558C3.5202 15.5016 4.37737 17.1053 5.63604 18.364C6.89472 19.6226 8.49836 20.4798 10.2442 20.8271C11.99 21.1743 13.7996 20.9961 15.4442 20.3149C17.0887 19.6337 18.4943 18.4802 19.4832 17.0001C20.4722 15.5201 21 13.78 21 12C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.387 3 12 3ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C20 14.1217 19.1572 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20Z'
            fill='black'
          />
        </g>
      </svg>
    ),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Check'>
          <path
            id='Vector'
            d='M17.8482 8.85887L18.2125 8.5054L17.8536 8.14645L17.3536 7.64645L17.0057 7.29864L16.6523 7.6407L10.8057 13.2986L8.35355 10.8464L8 10.4929L7.64645 10.8464L7.14645 11.3464L6.79289 11.7L7.14645 12.0536L10.4464 15.3536L10.7947 15.7018L11.1482 15.3589L17.8482 8.85887ZM20.5 11.5C20.5 15.9239 16.9239 19.5 12.5 19.5C8.07614 19.5 4.5 15.9239 4.5 11.5C4.5 7.07614 8.07614 3.5 12.5 3.5C16.9239 3.5 20.5 7.07614 20.5 11.5Z'
            fill='black'
            stroke='black'
          />
        </g>
      </svg>
    ),
    dateTimeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Calendar'>
          <path
            id='Vector'
            d='M17 4V3H16V4H8V3H7V4H3V21H16L21 16V4H17ZM8 5H16V7H8V5ZM4 5H7V7H4V5ZM20 15.33L15.44 20H15V15H20V15.33ZM20 14H14V20H4V8H7V9H8V8H16V9H17V8H20V14ZM20 7H17V5H20V7Z'
            fill='black'
          />
        </g>
      </svg>
    ),
    backIcon: (
      <svg width='13' height='16' viewBox='0 0 13 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Arrow'>
          <path
            id='Vector'
            d='M4.1 7.7L3.875 8L4.1 8.3L9.5 15.5L9.49025 15.5L3.87463 8L9.49025 0.5L9.5 0.5L4.1 7.7Z'
            fill='black'
            stroke='black'
          />
        </g>
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Check">
        <path
          id="Vector"
          d="M17.8482 8.85887L18.2125 8.5054L17.8536 8.14645L17.3536 7.64645L17.0057 7.29864L16.6523 7.6407L10.8057 13.2986L8.35355 10.8464L8 10.4929L7.64645 10.8464L7.14645 11.3464L6.79289 11.7L7.14645 12.0536L10.4464 15.3536L10.7947 15.7018L11.1482 15.3589L17.8482 8.85887ZM20.5 11.5C20.5 15.9239 16.9239 19.5 12.5 19.5C8.07614 19.5 4.5 15.9239 4.5 11.5C4.5 7.07614 8.07614 3.5 12.5 3.5C16.9239 3.5 20.5 7.07614 20.5 11.5Z"
          fill="black"
          stroke="black"
        />
      </g>
    </svg>
    `),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g id='Tooltip'>
          <path id='Vector' d='M11 7H13V9H11V7ZM11 11H13V17H11V11Z' fill='black' />
          <path
            id='Vector_2'
            d='M12 3C10.22 3 8.47991 3.52784 6.99987 4.51677C5.51983 5.50571 4.36628 6.91131 3.68509 8.55585C3.0039 10.2004 2.82567 12.01 3.17294 13.7558C3.5202 15.5016 4.37737 17.1053 5.63604 18.364C6.89472 19.6226 8.49836 20.4798 10.2442 20.8271C11.99 21.1743 13.7996 20.9961 15.4442 20.3149C17.0887 19.6337 18.4943 18.4802 19.4832 17.0001C20.4722 15.5201 21 13.78 21 12C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.387 3 12 3ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C20 14.1217 19.1572 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20Z'
            fill='black'
          />
        </g>
      </svg>
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#DAECFF",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  popup: {
    root: {
      fontFamily: getFontFamily("PorscheNext-Regular"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#403F45",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("PorscheNext-Regular"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  carInfo: {
    color: variables.colors.text.primary,
    fontWeight: "bold",
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.primary,
        fontWeight: "bold",
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.primary,
        fontWeight: "bold",
      },
      CoverageItemText: {
        color: variables.colors.primary.main,
      },
    },
    selected: {
      CoverageHeaderName: {
        fontWeight: "bold",
      },
      CoverageHeaderPrice: {
        fontWeight: "bold",
      },
      textColor: variables.colors.primary.dark,
      backgroundColor: variables.colors.transparent,
      borderWidth: 2,
    },
    descriptionCard: {
      backgroundColor: variables.colors.divider,
      borderColor: variables.colors.divider,
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.text.secondary,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
    },
    TopMargin: {
      "& .MuiTypography-body1": {
        color: variables.colors.primary.main,
      },
    },
  },
  statusIndicator: {
    borderRadius: 4,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(porsche);
